"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.hexToRgbaStyle = exports.hexToRgb = exports.hasAnyPermissions = exports.hasAllPermissions = exports.getMenuColumns = exports.getColumnDistribution = exports.convertWfmItemsToC1Format = exports.buildTenantSubdomainUrl = exports.buildC1Url = exports.browserDependentStyling = void 0;
exports.resolveNavbarItems = resolveNavbarItems;
var _detectBrowser = require("detect-browser");
/** Determines whether or not a User has all permissions required to access a route
 *  @param {string[]} userPermissions - An array of permissions that a User currently has
 *  @param {string[]} requiredPermissions - An array of permissions required to access a specific route/resource
*/
const hasAllPermissions = (userPermissions, requiredPermissions) => {
  return requiredPermissions.every(requiredPerm => userPermissions.includes(requiredPerm)) || requiredPermissions.length === 0;
};

/** Determines whether or not a User has one of many permissions required to access a route
 *  @param {string[]} userPermissions - An array of permissions that a User currently has
 *  @param {string[]} requiredPermissions - An array of permissions required to access a specific route/resource
*/
exports.hasAllPermissions = hasAllPermissions;
const hasAnyPermissions = (userPermissions, requiredPermissions) => {
  return requiredPermissions.some(requiredPerm => userPermissions.includes(requiredPerm)) || requiredPermissions.length === 0;
};

/**
 * Resolves a list of base navbar items given an application context
 * @param items base navbar items to be resolved
 * @param navbarArgs application context
 * @returns resolved navbar items
 */
exports.hasAnyPermissions = hasAnyPermissions;
function resolveNavbarItems(items, navbarArgs) {
  return items.map(baseItem => resolveNavbarItem(baseItem, navbarArgs)).filter(item => navbarArgs.showFilteredNavbar ? item.isAllowed : true);
}

/** Optionally take functions that can compute the final values of the navbar item
 *  @param {BaseNavbarItem} item - An unresolved navbar item to be resolved
 *  @param {NavbarArgs} navbarArgs - The NavbarArgs object passed down from the top level of the getNavbar call
*/
const resolveNavbarItem = (item, navbarArgs) => {
  const children = typeof item.children === 'function' ? resolveNavbarItems(item.children(navbarArgs), navbarArgs) : resolveNavbarItems(item.children ?? [], navbarArgs);
  return {
    key: item.key,
    external: item.external ?? false,
    isAllowed: typeof item.isAllowed === 'function' ? item.isAllowed(navbarArgs) : item.isAllowed,
    url: item.url(navbarArgs),
    children: navbarArgs.distributeColumns ? getMenuColumns(children, getColumnDistribution(children.length)) : children
  };
};

/**
 * Convert the WFM nav items into the C1 nav item format
 * @param wfmItems The WFM configuration object, exactly as it is returned from their API
 * @returns A collection of base navbar items; will be resolved later
 */
const convertWfmItemsToC1Format = (wfmItems, wfmBaseUrl) => {
  const PARENT_PATH_PATTERN = /^\.\.\//;
  return Object.values(wfmItems).map(item => ({
    key: item.name,
    url: () => wfmBaseUrl + '/web/' + item.url.replace(PARENT_PATH_PATTERN, ''),
    isAllowed: () => item.isPermitted,
    external: item.external
  }));
};

/**
 * Build the URL string for a C1 route. The resulting URL can be different depending on whether the navbar is 
 * within the C1 UI or not.
 * @param {string} c1BaseUrl The base URL for C1. This should not include a trailing slash.
 * @param {string} routePath The path to the route within C1.
 * @param {boolean} classic Whether the route is a classic route or not.
 * @param {boolean} withinC1 Whether the navbar is within the C1 UI or not. The default is true.
 * @returns The full URL string for the route.
 */
exports.convertWfmItemsToC1Format = convertWfmItemsToC1Format;
const buildC1Url = (c1BaseUrl, routePath, classic, withinC1 = true) => {
  let pathSeparator = '/#';
  if (classic && withinC1) {
    pathSeparator = '/classic/#';
  }
  const queryParams = withinC1 ? '' : '?iamRedirect=true';
  return c1BaseUrl + pathSeparator + routePath + queryParams;
};

/**
 * Build the URL string for a tenant subdomain route.
 * @param {string} tenantSubdomainUrl The base URL for the tenant subdomain.
 * @param {string} routePath The path to the route within the tenant subdomain.
 * @returns The full URL string for the route.
 */
exports.buildC1Url = buildC1Url;
const buildTenantSubdomainUrl = (tenantSubdomainUrl, routePath) => {
  // If the tenant subdomain URL is not provided or is empty, return an empty string
  if (!tenantSubdomainUrl || tenantSubdomainUrl.trim() === '') {
    return '';
  }

  // Remove trailing slashes from base URL if present
  const normalizedBaseUrl = tenantSubdomainUrl.replace(/\/+$/, '');

  // If the route path is not provided or is empty, return just the base URL
  if (!routePath || routePath.trim() === '') {
    return normalizedBaseUrl;
  }

  // Ensure path starts with a slash and normalize it
  const normalizedPath = routePath.startsWith('/') ? routePath : '/' + routePath;
  return normalizedBaseUrl + normalizedPath;
};

/**
  This algorithm determines the optimal column distribution for dropdowns
  Ex. 13 items, 5 max items per column should produce --> [ 5, 4, 4 ]
  Ex. 12 items, 5 max items per column should produce --> [ 4, 4, 4 ]
  N.B. for E.G. 13 items, UX prefers [5,4,4] not [5,5,3]
  See the tests in NavBarHelper.test.js for the desired results for each number of items.
  @param {number} numItems - the total number of items that need to be displayed
  @returns An array with the number of items which should exist in each column
*/
exports.buildTenantSubdomainUrl = buildTenantSubdomainUrl;
const getColumnDistribution = numItems => {
  // This number can be parameterized. The algorithm will still work. But the UX calls for 5
  const maxItemsPerColumn = 5;
  // Find the number of columns that would be needed to evenly distribute items
  // E.g. 7 items, 5 per column would yield 2 columns
  const numCols = Math.ceil(numItems / maxItemsPerColumn);
  // Create an array with the number of columns
  // E.g. 7 items, 5 per column would yield [0,0]
  const columnData = Array(numCols).fill(0);
  // Create an array of length num items so that we can iterate over it
  const itemsArr = Array(numItems).fill(0);

  /*
    Basically this algorthm goes like this for e.g. 5 items and 3 max items per column:
    We have itemsArr which looks like [0,0,0,0,0,0]
    We have columnData [0,0]
    For each item in itemsArr we go from right to left adding to the columnData representation
    1) [1,0] 2) [1,1] 3) [2,1] ... until we get to [3,2]
    The result is what we wish our columns to look like (in this case 3 items in the first column, 2 in the second)
    Probably reduce is techically the correct pattern here, but this is more straightforward.
  */
  let index = 0;
  itemsArr.forEach(() => {
    columnData[index] = columnData[index] + 1;
    index++;
    if (index >= numCols) {
      index = 0;
    }
  });
  return columnData;
};

/** This function 'hydrates' our column data structure from `getColumnDistribution` with the actual data
 *  @param {Array<any>} menuItems - The navbar menu items to be hydrated into the column distribution array
 *  @param {Array<any>} colDistribution - The resulting columnData object returned from `getColumnDistribution`
 */
exports.getColumnDistribution = getColumnDistribution;
const getMenuColumns = (menuItems, colDistribution) => {
  // Make each an array for each column
  const container = colDistribution.map(() => []);
  let index = 0;
  // Fill each column with the appropriate number of items
  // E.g. [
  //        [ { itemOne}, {itemTwo}, {itemThree}],
  //        [ { itemFour}, {itemFive} ]
  // ]
  colDistribution.forEach((col, colIndex) => {
    const colArr = new Array(col).fill(0);
    colArr.forEach(() => {
      container[colIndex].push(menuItems[index]);
      index++;
    });
  });
  return container;
};

/* Internet Explorer renders the top left corner of the <Paper> element at the bottom right
  * corner of it's parent element making it very difficult to hover over. This function returns
  * styling to shift the position of the element to better line up with its parent.
  * Works in Internet Explorer 6-11. http://jira/browse/SAAS-51568 */
exports.getMenuColumns = getMenuColumns;
const browserDependentStyling = () => {
  const browserName = (0, _detectBrowser.detect)()?.name;
  if (browserName === 'ie') {
    return {
      left: 0
    }; // IE adds something called offset, in addition to margin & padding (hence the top left corner rendering issue). By setting left: 0, the offset is removed
  } else {
    return {};
  }
};
exports.browserDependentStyling = browserDependentStyling;
const hexToRgb = (hex, fallback) => {
  if (!hex) {
    return fallback ? fallback : {
      r: 255,
      g: 255,
      b: 255
    };
  }
  let normalizedHex = hex;
  if (hex && hex.length === 4) {
    const hexOnly = hex.substring(1, 4);
    const r = hexOnly[0],
      g = hexOnly[1],
      b = hexOnly[2];
    normalizedHex = `#${r}${r}${g}${g}${b}${b}`;
  }
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalizedHex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : {
    r: null,
    g: null,
    b: null
  };
};

// Alpha can be a string or a decimal
// Hex should be a string beginning with a #
// fallback should be a hex value
exports.hexToRgb = hexToRgb;
const hexToRgbaStyle = (hex, alpha, fallback) => {
  const {
    r,
    g,
    b
  } = hexToRgb(hex, fallback);
  const rgbaString = `rgba(${r},${g},${b},${alpha})`;
  return rgbaString;
};
exports.hexToRgbaStyle = hexToRgbaStyle;
//# sourceMappingURL=utils.js.map